export const AUTH_INFO = {
  headerName: "key",
  description:
    "Every bank-facing SEC@R endpoint requires a shared API key in the request header. SEC@R stores one key per partner bank.",
  example: "key: your-partner-bank-api-key",
  failure: "401 Unauthorized — Invalid API key",
};

export const DEFAULT_PAYMENT_POLICY = {
  title: "Default payment posture (SEC@R customers)",
  points: [
    "Payment channels (netbanking, cards, transfers, bill pay, QR pay, cheque book) are OFF by default for SEC@R-linked customers.",
    "They turn ON only while an active SEC@R payment window is open (default 120 seconds after wallet/device open).",
    "When the window expires — or SEC@R calls disable-services — payment channels auto-return to OFF.",
    "Non-payment services (UPI info flags, statements, loans, fixed deposits) are informational in verify-user; they are not forced OFF by the payment window.",
    "SEC@R-controlled toggles on the bank netbanking dashboard are read-only during the window; the customer cannot manually override them.",
  ],
};

export const DEV_SIMULATOR = {
  title: "Local simulator (Infinicue dev)",
  rows: [
    { service: "SEC@R API", port: "8003", note: "FastAPI — events, payment window, netbanking login broker" },
    { service: "Bank A", port: "8201", note: "Partner bank simulator + netbanking UI" },
    { service: "Bank B", port: "8202", note: "Second partner bank simulator" },
    { service: "Metro", port: "8081", note: "Transit simulator (optional)" },
    { service: "Admin web", port: "3000", note: "SEC@R admin / this integration guide" },
  ],
  adbReverse:
    "Physical Android phone: adb reverse tcp:8003 tcp:8003 (and 8201, 8202, 8081) so the app can reach your PC.",
};

export const SETUP_CONFIG = {
  title: "SEC@R configuration (what Infinicue needs from the bank)",
  rows: [
    { key: "BANK_X_BASE_URL", example: "https://api.yourbank.com", note: "Public HTTPS base URL of your bank API" },
    { key: "BANK_X_API_KEY", example: "<shared-secret>", note: "Same key your bank validates in the key header" },
    { key: "PAYMENT_GEO_RADIUS_METERS", example: "10.0", note: "Device ↔ transaction distance allowed for auto-approve (default 10 m)" },
    { key: "PAYMENT_WINDOW_SECONDS", example: "120", note: "How long payment methods stay ON after wallet open (default 120 s)" },
  ],
  bankBasePath: "{BANK_BASE_URL}/api/secar",
};

export const BANK_DASHBOARD = {
  title: "Bank netbanking dashboard — live service toggles",
  description:
    "Partner bank UIs (simulators on :8201 / :8202) show per-customer service switches. SEC@R-controlled payment rows update in real time when the payment window opens or closes.",
  endpoints: [
    {
      method: "GET",
      path: "/ui/service-prefs/live",
      auth: "Customer session cookie (logged-in bank user)",
      returns: "{ revision, window_active, prefs }",
      note: "Zero-DB in-memory read — safe to poll every 1–2 s while dashboard is open.",
    },
    {
      method: "GET",
      path: "/ui/service-prefs/stream",
      auth: "Customer session cookie",
      returns: "text/event-stream — prefs JSON on each SEC@R enable/disable",
      note: "Push path; dashboard also polls /live as fallback.",
    },
  ],
  uiRules: [
    "Rows with data-service-id matching netbanking, transfers, debit_card, credit_card, bill_pay, qr_pay, cheque_book render as read-only when SEC@R controls them.",
    "When window_active is false, payment toggles display OFF even if stale DB prefs exist.",
    "When SEC@R calls enable-services, toggles flip ON instantly and SSE notifies open dashboards.",
    "When the 120 s timer fires or disable-services runs, toggles flip OFF and dashboards receive another push.",
  ],
};

export const SERVICE_IDS = [
  { id: "netbanking", name: "Netbanking", payment: true },
  { id: "transfers", name: "IMPS / NEFT", payment: true },
  { id: "debit_card", name: "Debit Card", payment: true },
  { id: "credit_card", name: "Credit Card", payment: true },
  { id: "upi", name: "UPI", payment: false },
  { id: "bill_pay", name: "Bill Pay", payment: true },
  { id: "qr_pay", name: "QR Pay", payment: true },
  { id: "cheque_book", name: "Cheque Book", payment: true },
  { id: "fixed_deposit", name: "Fixed Deposit", payment: false },
  { id: "loans", name: "Loans", payment: false },
  { id: "statements", name: "Statements", payment: false },
];

export const PAYMENT_SERVICE_IDS = SERVICE_IDS.filter((s) => s.payment).map((s) => s.id);

export const INTEGRATION_FLOW = [
  {
    step: 1,
    phase: "User registration",
    actor: "Mobile app → SEC@R",
    bankApi: null,
    secarEndpoint: "POST /v2/signup",
    description: "User creates a SEC@R account. Profile is encrypted and stored. Verification email is sent.",
    notes: "No bank API call at this stage.",
  },
  {
    step: 2,
    phase: "Email verification",
    actor: "Browser / Mobile → SEC@R",
    bankApi: null,
    secarEndpoint: "GET /v1/verifyUser?verificationToken=…",
    description: "User clicks the email link. SEC@R marks isVerify=true and issues a passkey.",
    notes: "Socket event user_verified is emitted to the mobile app.",
  },
  {
    step: 3,
    phase: "SEC@R onboarding & bank lookup",
    actor: "Mobile app → SEC@R → All banks",
    bankApi: "POST /api/secar/verify-user",
    secarEndpoint: "POST /v1/secar-onboarding-check",
    description:
      "First-time setup: SEC@R verifies identity (mock PAN/Aadhaar in simulator) and calls verify-user on every registered bank in parallel.",
    notes: "Returns which banks recognize the customer and current service flags (CC, DC, UPI, etc.).",
  },
  {
    step: 4,
    phase: "Optional bank re-check",
    actor: "Mobile app → SEC@R → All banks",
    bankApi: "POST /api/secar/verify-user",
    secarEndpoint: "POST /v1/verify-bank-user",
    description: "Lightweight re-verify by mobile + full name when the app needs fresh bank service flags.",
    notes: "Same verify-user contract as step 3.",
  },
  {
    step: 5,
    phase: "Wallet / device open → payment window",
    actor: "BLE device → Mobile → SEC@R + Banks",
    bankApi: "POST /api/secar/enable-services",
    secarEndpoint: "POST /v1/open-payment-window · POST /v1/record-event (open)",
    description:
      "When the smart wallet or card holder opens, the mobile app fires a fast payment-window open. SEC@R enables payment services at all partner banks and starts a 120-second timer. Banks mark the customer payment window active and flip dashboard toggles ON.",
    notes:
      "Dual path: (1) SEC@R POST /v1/open-payment-window fans out to banks; (2) mobile may also POST enable-services directly when adb reverse reaches bank ports. Services stay OFF outside this window.",
  },
  {
    step: 6,
    phase: "Payment attempt (geo-gated)",
    actor: "Mobile → SEC@R → Bank(s)",
    bankApi: "enable-services OR disable-services + suspicious-alert",
    secarEndpoint: "POST /v1/record-event (payment intent) or POST /v1/authorize-transaction",
    description:
      "If device GPS is within 10 m of the transaction location → enable-services. If geo mismatch → disable-services + suspicious-alert; user gets a confirmation prompt.",
    notes: "Radius is configurable via PAYMENT_GEO_RADIUS_METERS.",
  },
  {
    step: 7,
    phase: "Suspicious alert confirmation",
    actor: "Mobile → SEC@R → Bank(s)",
    bankApi: "POST /api/secar/enable-services or disable-services",
    secarEndpoint: "POST /v1/confirm-payment-alert",
    description: "User taps Yes/No on the geo-mismatch alert. Yes + location match → enable; No → disable and block.",
    notes: null,
  },
  {
    step: 8,
    phase: "Wallet close",
    actor: "BLE device → Mobile → SEC@R",
    bankApi: "POST /api/secar/disable-services (after timer only)",
    secarEndpoint: "POST /v1/wallet-close · POST /v1/record-event (close)",
    description:
      "Wallet closed event is recorded. Payment services remain ON until PAYMENT_WINDOW_SECONDS elapses — close does not immediately disable banks.",
    notes: "POST /v1/wallet-close notifies SEC@R; auto-disable still follows the timer unless emergency lock is used.",
  },
  {
    step: 9,
    phase: "Netbanking login via SEC@R",
    actor: "Browser (bank) → SEC@R → Mobile app",
    bankApi: "Bank UI redirects to SEC@R verify flow",
    secarEndpoint:
      "POST /v1/netbanking-login/start · POST /v1/netbanking-login/confirm · GET /v1/netbanking-login/status/{id}",
    description:
      "Customer logs into partner bank netbanking. Bank creates a SEC@R challenge; customer approves on the SEC@R mobile app (push / poll). On approve, bank session completes. Location capture was removed from Bank A login UI — approval is device-based.",
    notes:
      "Mobile polls POST /v1/netbanking-login-pending while waiting. FCM/socket delivers the approve/deny prompt when available.",
  },
  {
    step: 10,
    phase: "Auto-disable (timer expiry)",
    actor: "SEC@R → Bank(s)",
    bankApi: "POST /api/secar/disable-services",
    secarEndpoint: "(internal timer after wallet open)",
    description: "After PAYMENT_WINDOW_SECONDS, SEC@R disables payment services at banks where the customer exists.",
    notes: "Requires user_consent: true in the disable payload (SEC@R sends true for auto-close).",
  },
  {
    step: 11,
    phase: "Emergency lock (user consent)",
    actor: "Mobile → SEC@R → Bank(s)",
    bankApi: "verify-user → disable-services",
    secarEndpoint: "POST /v1/secure-all-bank-payments",
    description: "User explicitly secures all accounts: SEC@R verifies customer at each bank, then disables all payment services.",
    notes: "user_consent must be true.",
  },
  {
    step: 12,
    phase: "Admin monitoring",
    actor: "Admin console → SEC@R → Bank(s)",
    bankApi: "GET /api/secar/stats",
    secarEndpoint: "POST /v1/admin-console-overview",
    description: "Admin dashboard polls bank stats (customer count, account count, online status).",
    notes: null,
  },
];

export const BANK_APIS = [
  {
    id: "verify-user",
    order: 1,
    method: "POST",
    path: "/api/secar/verify-user",
    title: "Verify customer",
    whenCalled: [
      "SEC@R onboarding check (step 3)",
      "Optional re-verify (step 4)",
      "Before emergency lock — find which banks know the customer",
      "Before auto-disable — only disable at banks where customer exists",
    ],
    request: {
      mobile: "9876543210",
      full_name: "Jane Doe",
    },
    responseFound: {
      exists: true,
      bank_code: "BANK_A",
      bank_name: "Partner Bank A",
      user_id: 42,
      mobile: "9876543210",
      full_name: "Jane Doe",
      name_match: true,
      services: {
        netbanking: true,
        transfers: true,
        debit_card: true,
        credit_card: false,
        upi: true,
        bill_pay: true,
        cheque_book: true,
        fixed_deposit: true,
        loans: true,
        statements: true,
        qr_pay: true,
      },
      holdings: {
        account: true,
        debit_card: true,
        credit_line: false,
        credit_card: false,
      },
    },
    responseNotFound: {
      exists: false,
      bank_code: "BANK_A",
      bank_name: "Partner Bank A",
      mobile: "9876543210",
      full_name: "Jane Doe",
      name_match: false,
      services: {
        netbanking: false,
        transfers: false,
        debit_card: false,
        credit_card: false,
        upi: false,
        bill_pay: false,
        cheque_book: false,
        fixed_deposit: false,
        loans: false,
        statements: false,
        qr_pay: false,
      },
      holdings: {
        account: false,
        debit_card: false,
        credit_line: false,
        credit_card: false,
      },
    },
    errors: [
      { code: 401, detail: "Invalid API key" },
    ],
    matchingRules: [
      "Mobile: match on last 10 digits (strip country code / non-digits)",
      "Full name: normalized lowercase; exact match or substring match",
      "Return HTTP 200 with exists:false when customer not found (soft fail)",
    ],
  },
  {
    id: "stats",
    order: 2,
    method: "GET",
    path: "/api/secar/stats",
    title: "Bank statistics",
    whenCalled: ["Admin console overview (step 12)"],
    request: null,
    responseFound: {
      bank_code: "BANK_A",
      bank_name: "Partner Bank A",
      status: "online",
      customer_count: 150,
      account_count: 200,
    },
    responseNotFound: null,
    errors: [{ code: 401, detail: "Invalid API key" }],
    matchingRules: ["No request body. Auth header only."],
  },
  {
    id: "enable-services",
    order: 3,
    method: "POST",
    path: "/api/secar/enable-services",
    title: "Enable payment services",
    whenCalled: [
      "Wallet open — 120 s payment window (step 5)",
      "Payment authorized — geo match (step 6)",
      "User confirms suspicious alert + geo match (step 7)",
    ],
    request: {
      mobile: "9876543210",
      full_name: "Jane Doe",
      user_id: 42,
      services_to_enable: PAYMENT_SERVICE_IDS,
    },
    responseFound: {
      status: "ok",
      bank_code: "BANK_A",
      user_id: 42,
      enabled: {
        netbanking: true,
        transfers: true,
        debit_card: true,
        credit_card: true,
        upi: true,
        bill_pay: true,
        qr_pay: true,
        cheque_book: true,
      },
    },
    responseNotFound: null,
    errors: [
      { code: 401, detail: "Invalid API key" },
      { code: 404, detail: "Bank customer not found" },
      { code: 400, detail: "Unknown service: <service_id>" },
    ],
    matchingRules: [
      "Turn ON only the service IDs listed in services_to_enable",
      "Optional user_id — when present, skip DB lookup (fast path used by SEC@R after first verify)",
      "Side effect: opens SEC@R payment window for that bank user (default 120 s); dashboard toggles update via SSE",
      "Persist per-customer service preferences in your core banking layer",
      "If no payment window is active, effective prefs for SEC@R-controlled channels must read as OFF",
    ],
  },
  {
    id: "disable-services",
    order: 4,
    method: "POST",
    path: "/api/secar/disable-services",
    title: "Disable payment services",
    whenCalled: [
      "Payment window timer expiry (step 10)",
      "User declines suspicious alert (step 7)",
      "Geo mismatch during payment (step 6)",
      "Emergency lock with user consent (step 11)",
      "Targeted disable via SEC@R request-disable-bank-services",
    ],
    request: {
      mobile: "9876543210",
      full_name: "Jane Doe",
      user_id: 42,
      services_to_disable: PAYMENT_SERVICE_IDS,
      user_consent: true,
    },
    responseFound: {
      status: "ok",
      bank_code: "BANK_A",
      user_id: 42,
      user_consent: true,
      disabled: {
        netbanking: false,
        debit_card: false,
        upi: false,
      },
      message: "Payment methods disabled per SEC@R user consent.",
    },
    responseNotFound: null,
    errors: [
      { code: 401, detail: "Invalid API key" },
      { code: 400, detail: "User consent required to disable payment services" },
      { code: 404, detail: "Bank customer not found" },
      { code: 400, detail: "Unknown service: <service_id>" },
    ],
    matchingRules: [
      "user_consent must be true — reject with 400 if false",
      "SEC@R always sends user_consent:true for auto-close and emergency flows",
      "Optional user_id for fast disable without DB lookup",
      "Clears SEC@R payment window; dashboard toggles return to OFF for payment channels",
    ],
  },
  {
    id: "suspicious-alert",
    order: 5,
    method: "POST",
    path: "/api/secar/suspicious-alert",
    title: "Log suspicious activity",
    whenCalled: [
      "Geo mismatch — immediate block (step 6)",
      "Geo mismatch — pending user confirmation (step 6)",
    ],
    request: {
      mobile: "9876543210",
      full_name: "Jane Doe",
      reason: "geo_mismatch",
      device_latitude: 12.9716,
      device_longitude: 77.5946,
      transaction_latitude: 12.98,
      transaction_longitude: 77.6,
      distance_meters: 1250.5,
    },
    responseFound: {
      status: "logged",
      bank_code: "BANK_A",
      user_id: 42,
      reason: "geo_mismatch",
      distance_meters: 1250.5,
      action: "payment_methods_frozen",
    },
    responseNotFound: null,
    errors: [{ code: 401, detail: "Invalid API key" }],
    matchingRules: [
      "Log the alert in your fraud / audit system",
      "reason values include geo_mismatch and geo_mismatch_pending",
      "Disable is handled separately via disable-services",
    ],
  },
];

export const NOT_BANK_APIS = [
  "/api/auth/register",
  "/api/auth/login",
  "/api/accounts/*",
  "/api/transfers",
  "/api/debit-cards/*",
  "/api/credit-cards/*",
  "/api/services (customer JWT — not SEC@R key header)",
  "/ui/login/* (browser netbanking UI — uses bank session cookies, not SEC@R key)",
  "/ui/service-prefs/live (customer dashboard poll — session cookie)",
  "/ui/service-prefs/stream (customer dashboard SSE — session cookie)",
];
